import * as React from "react";

import * as ReactDOM from "react-dom";

export interface IPortalProps extends React.Props<Portal> {
    container?: HTMLElement;
}

/**
 * The `<Portal/>` component renders its children into a new "subtree" outside of current component hierarchy.
 * You can think of it as a declarative `appendChild()`, or jQuery's `$.fn.appendTo()`.
 * The children of `<Portal/>` component will be appended to the `container` specified.
 */
export class Portal extends React.Component<IPortalProps, any> {
    private _overlayTarget: HTMLDivElement;

    private _portalContainerNode: HTMLElement;

    private _overlayInstance: Element;

    constructor(props: IPortalProps) {
        super(props);
    }

    public render(): JSX.Element {
        return null;
    }

    public componentDidMount(): void {
        this._renderOverlay();
    }

    public componentDidUpdate(): void {
        this._renderOverlay();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IPortalProps): void {
        if (this._overlayTarget && nextProps.container !== this.props.container) {
            this._portalContainerNode.removeChild(this._overlayTarget);
            this._portalContainerNode = this.props.container;
            this._portalContainerNode.appendChild(this._overlayTarget);
        }
    }

    public componentWillUnmount(): void {
        this._unrenderOverlay();
        this._unmountOverlayTarget();
    }

    private _mountOverlayTarget(): void {
        if (!this._overlayTarget) {
            this._overlayTarget = document.createElement("div");
            this._portalContainerNode = this.props.container;
            this._portalContainerNode.appendChild(this._overlayTarget);
        }
    }

    private _unmountOverlayTarget(): void {
        if (this._overlayTarget && this._portalContainerNode) {
            this._portalContainerNode.removeChild(this._overlayTarget);
            this._overlayTarget = null;
        }
        this._portalContainerNode = null;
    }

    private _renderOverlay(): void {
        const overlay = !this.props.children ? null : React.Children.only(this.props.children);

        // Save reference for future access.
        if (overlay !== null) {
            this._mountOverlayTarget();
            this._overlayInstance = ReactDOM.unstable_renderSubtreeIntoContainer(
                this,
                overlay as any,
                this._overlayTarget
            );
        } else {
            // Unrender if the component is null for transitions to null
            this._unrenderOverlay();
            this._unmountOverlayTarget();
        }
    }

    private _unrenderOverlay(): void {
        if (this._overlayTarget) {
            ReactDOM.unmountComponentAtNode(this._overlayTarget);
            this._overlayInstance = null;
        }
    }
}
